import { useCallback, useEffect, useRef, useState, type SVGProps } from "react";
import { useT } from "../i18n/index.js";
import { cn } from "../lib/cn";

/**
 * A search box.
 *
 * Typing is local and instant; the query it hands upward is debounced, because
 * the search itself runs over everything the device holds (SPEC §3: in an
 * encrypted space the server has ciphertext, so there is nobody else to ask).
 * 150 ms is enough to skip the intermediate states of a word without anyone
 * noticing a wait.
 *
 * The owner holds the debounced query. This keeps its own copy of what is in
 * the box so a keystroke never waits for a round trip through the parent, and
 * adopts the owner's value again whenever that changes for some other reason —
 * a reset, a restored query — rather than fighting it.
 */

const DEBOUNCE_MS = 150;

const SearchIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </svg>
);

const ClearIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <circle cx="12" cy="12" r="8.5" />
    <path d="m9.2 9.2 5.6 5.6M14.8 9.2l-5.6 5.6" />
  </svg>
);

export interface SearchFieldProps {
  /** The debounced query, held by the owner. */
  value: string;
  onChange(next: string): void;
  placeholder?: string;
  /** Accessible name. The field carries no visible label on a phone. */
  label?: string;
  autoFocus?: boolean;
  className?: string;
}

export function SearchField({
  value,
  onChange,
  placeholder,
  label,
  autoFocus = false,
  className,
}: SearchFieldProps) {
  const t = useT();
  const [text, setText] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // What we last handed upward. Without it the effect below would treat our own
  // debounced emission as an outside change and reset the box mid-word.
  const emitted = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setText(value);
  }, [value]);

  useEffect(() => {
    if (text === emitted.current) return;

    const timer = setTimeout(() => {
      emitted.current = text;
      onChangeRef.current(text);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [text]);

  /** Clearing is the one thing that must not wait for the debounce. */
  const clear = useCallback(() => {
    setText("");
    emitted.current = "";
    onChangeRef.current("");
    inputRef.current?.focus();
  }, []);

  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-pill bg-surface-alt px-4",
        "transition-colors focus-within:bg-surface",
        className,
      )}
    >
      <SearchIcon aria-hidden="true" className="size-5 shrink-0 text-muted" />
      <input
        ref={inputRef}
        // A real search input: iOS gives it the rounded keyboard and a "Найти"
        // return key, and assistive tech announces it as a search box.
        type="search"
        inputMode="search"
        aria-label={label ?? t("search.fieldLabel")}
        placeholder={placeholder ?? t("search.fieldPlaceholder")}
        value={text}
        autoFocus={autoFocus}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          // Escape empties the box rather than closing the screen; Safari also
          // clears a type="search" input itself, and this keeps the two in step.
          event.preventDefault();
          clear();
        }}
        className={cn(
          "min-h-[var(--touch)] w-full min-w-0 bg-transparent py-2 outline-none",
          "placeholder:text-muted/70",
          // Safari draws its own clear button on top of ours.
          "[&::-webkit-search-cancel-button]:hidden",
        )}
      />
      {text ? (
        <button
          type="button"
          onClick={clear}
          aria-label={t("search.fieldClear")}
          className="-mr-1 grid size-[var(--touch)] shrink-0 place-items-center rounded-card text-muted transition-colors hover:bg-surface-alt"
        >
          <ClearIcon className="size-5" />
        </button>
      ) : null}
    </div>
  );
}
